import { repositoryMovedNotice } from '@/lib/desktop-shell/cloud-repository';
import type { DesktopShellStore } from '@/lib/desktop-shell/store';
import { sourceControlRepositoryFullName } from '@/lib/sidebar-threads';
import {
  fetchSourceControlRepositoryBranches,
  resolveSourceControlRepository,
  type SourceControlRepository,
} from '@/lib/source-control-connector';

export type CloudHandoffOptions = {
  resolveCloudHandoffRepository: () => Promise<SourceControlRepository | null>;
  loadCloudHandoffOptions: () => Promise<void>;
};

/**
 * Resolves the source control repository and remote branch list backing a cloud
 * handoff. Workspace + git status come from the store (read lazily); the cloud
 * handoff dialog owns the resolved repository / branches, so its current value
 * and setters are injected by the caller.
 */
export function createCloudHandoffOptions({
  store,
  cloudHandoffRepository,
  cloudHandoffBranches,
  setCloudHandoffRepository,
  setCloudHandoffBranches,
  setCloudHandoffRemoteNotice,
}: {
  store: DesktopShellStore;
  cloudHandoffRepository: SourceControlRepository | null;
  cloudHandoffBranches: readonly string[];
  setCloudHandoffRepository: (repository: SourceControlRepository | null) => void;
  setCloudHandoffBranches: (branches: readonly string[]) => void;
  setCloudHandoffRemoteNotice: (notice: string | null) => void;
}): CloudHandoffOptions {
  /**
   * The folder's repository on the target server's forges, or null when none reaches it (no
   * remote, or no forge connected there): the folder then goes as it is, its base commit with it.
   */
  async function resolveCloudHandoffRepository(): Promise<SourceControlRepository | null> {
    const { gitStatus, workspace, remoteExecutionTarget } = store.getState();
    // The repository is resolved on the source control of the server that runs the target.
    const cloud = remoteExecutionTarget?.cloud ?? false;
    if (cloudHandoffRepository !== null && (cloudHandoffRepository.cloud ?? false) === cloud) {
      return cloudHandoffRepository;
    }
    const fullName = sourceControlRepositoryFullName(gitStatus?.remoteUrl ?? workspace);
    if (fullName === null) return null;
    const repository = await resolveSourceControlRepository(
      gitStatus?.remoteUrl ?? fullName,
      cloud,
    ).catch(() => null);
    if (repository === null) return null;
    setCloudHandoffRemoteNotice(repositoryMovedNotice(fullName, repository.fullName));
    setCloudHandoffRepository(repository);
    return repository;
  }

  async function loadCloudHandoffOptions(): Promise<void> {
    const { workspace } = store.getState();
    if (workspace === null) return;
    const repository = await resolveCloudHandoffRepository();
    if (repository === null || cloudHandoffBranches.length > 0) return;
    setCloudHandoffBranches(await fetchSourceControlRepositoryBranches(repository));
  }

  return { resolveCloudHandoffRepository, loadCloudHandoffOptions };
}
